import { useState } from "react";
import { ArrowLeft, Loader2, Printer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { modeloDaAnamnese } from "@bookdental/schemas";
import { Button, Textarea, VisualizacaoDaAnamnese } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { abrirImpressao } from "@/components/production/impressao";
import { SelosDeAlerta } from "./SelosDeAlerta";
import { CSS_DA_ANAMNESE, htmlDaAnamnese } from "./impressao";

const quando = (d: Date | string | null) => (d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

/**
 * Anamnese respondida em modo leitura: respostas, evidências da assinatura, revisão do dentista
 * (trava a anamnese) e adendos — a correção depois de revisada, sem mexer no original.
 */
export function LeituraDaAnamnese({ anamneseId, onVoltar }: { anamneseId: string; onVoltar: () => void }) {
  const utils = trpc.useUtils();
  const podePreencher = usePermissao("anamnese.manage");
  const q = trpc.anamnese.obter.useQuery({ id: anamneseId });
  const [observacao, setObservacao] = useState("");
  const [adendo, setAdendo] = useState("");
  const atualizar = () => {
    utils.anamnese.obter.invalidate({ id: anamneseId });
    utils.anamnese.listar.invalidate();
  };
  const revisar = trpc.anamnese.revisar.useMutation({ onSuccess: () => (atualizar(), toast.success("Anamnese revisada.")), onError: (e) => toast.error(e.message) });
  const incluirAdendo = trpc.anamnese.adendo.useMutation({ onSuccess: () => (atualizar(), setAdendo(""), toast.success("Adendo incluído.")), onError: (e) => toast.error(e.message) });

  if (!q.data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  const a = q.data;
  const modelo = modeloDaAnamnese(a.modelo, a.versaoDoModelo);
  const imprimir = async () => {
    // Janela aberta no clique: depois do await o navegador bloquearia como pop-up.
    const janela = abrirImpressao(900, 1000);
    if (!janela) return toast.error("Libere as janelas pop-up para imprimir.");
    try {
      const cab = await utils.anamnese.paraImprimir.fetch({ id: a.id });
      janela.escrever(`Anamnese - ${cab.paciente.name}`, CSS_DA_ANAMNESE, htmlDaAnamnese({ ...cab, ...a, modelo }));
    } catch {
      janela.fechar();
      toast.error("Não foi possível gerar a impressão.");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onVoltar} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800">
          <ArrowLeft className="h-4 w-4" /> Anamneses
        </button>
        <Button variant="outline" onClick={imprimir}>
          <Printer className="mr-1.5 h-4 w-4" /> Imprimir
        </Button>
      </div>

      <div className="space-y-2 rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm dark:border-border dark:bg-muted/40">
        <SelosDeAlerta alertas={a.alertas} />
        <p className="text-zinc-700 dark:text-foreground">
          Respondida em <b>{quando(a.respondidaEm)}</b> por{" "}
          {a.respondidaPor === "RESPONSAVEL"
            ? `${a.responsavelNome} (responsável${a.responsavelParentesco ? `, ${a.responsavelParentesco}` : ""})`
            : a.respondidaPor === "EQUIPE"
              ? `${a.assinatura?.nome ?? "equipe"}, na clínica`
              : "o próprio paciente"}
        </p>
        {a.assinatura && (
          <p className="flex items-start gap-1.5 text-[11.5px] text-zinc-500">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Assinado eletronicamente por {a.assinatura.nome}
              {a.assinatura.ip ? ` · IP ${a.assinatura.ip}` : ""} · integridade {a.assinatura.hash?.slice(0, 16)}…
            </span>
          </p>
        )}
      </div>

      <VisualizacaoDaAnamnese modelo={modelo} respostas={a.respostas} />

      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Revisão do cirurgião-dentista</h4>
        {a.status === "REVISADA" ? (
          <p className="text-sm text-zinc-700 dark:text-foreground">
            Revisada por <b>{a.revisadaPorNome}</b> em {quando(a.revisadaEm)}.{a.observacaoDaRevisao && <span className="block text-zinc-600">{a.observacaoDaRevisao}</span>}
          </p>
        ) : podePreencher ? (
          <div className="space-y-2">
            <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Observações da revisão (opcional)" maxLength={2000} rows={2} />
            <Button onClick={() => revisar.mutate({ id: a.id, observacao: observacao || null })} disabled={revisar.isPending}>
              Marcar como revisada
            </Button>
          </div>
        ) : (
          <p className="text-sm text-amber-700">Aguardando a revisão do cirurgião-dentista.</p>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Adendos</h4>
        {a.adendos.length === 0 && <p className="text-xs text-zinc-500">Nenhuma correção. A anamnese revisada não muda — correções entram aqui, com autor e data.</p>}
        {a.adendos.map((d) => (
          <p key={d.id} className="rounded-lg border border-zinc-200 p-2.5 text-sm dark:border-border">
            <span className="block text-[11px] text-zinc-500">
              {quando(d.criadoEm)} · {d.autorNome}
            </span>
            {d.texto}
          </p>
        ))}
        {podePreencher && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Textarea value={adendo} onChange={(e) => setAdendo(e.target.value)} placeholder="Nova correção ou informação" maxLength={2000} rows={2} className="flex-1" />
            <Button variant="outline" onClick={() => incluirAdendo.mutate({ id: a.id, texto: adendo })} disabled={adendo.trim().length < 2 || incluirAdendo.isPending}>
              Incluir adendo
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
